import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl, defineMessage} from 'react-intl'
import {
    Stack,
    useDisclosure,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {ToggleCard} from '../../../../components/toggle-card'
import {remCalc} from '../../../../theme/_utils'

import AdyenCheckout from '@adyen/adyen-web'
import {useAccessToken, useCustomerId} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useAdyenCheckout} from '../../../../vendors/adyen'
import {useLocaleCountry} from '../../../../hooks/use-locale-custom'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import useMutationObserver from '../../../../custom-hooks/use-mutation-observer'

import {PaymentIcon} from '../../../../components/icons'
import {createRoot} from 'react-dom/client'
import {ErrorModal} from '../../../../components/modals/error-modal'

const AddCard = ({closeModal, updateRender}) => {
    const {adyenPaymentMethods, adyenEnvironment, locale} = useAdyenCheckout()
    const [isSubmittingPayment] = useState(false)
    const [showCard, setShowCard] = useState(true)
    const [reload, setReload] = useState(false)
    const customerId = useCustomerId()
    const {getTokenWhenReady} = useAccessToken()
    const {site} = useMultiSite()

    const adyenStyle = useMultiStyleConfig('Adyen', {variant: 'paymentMethods'})

    const {isOpen, onOpen, onClose} = useDisclosure()
    const error = useRef()
    const paymentContainer = useRef(null)
    const fromActionRef = useRef(null)
    const {data: customer} = useCurrentCustomer()
    const intl = useIntl()
    const {formatMessage} = useIntl()
    const currentCountry = useLocaleCountry().toUpperCase()

    let checkout

    useEffect(() => {
        if (reload) {
            setTimeout(() => {
                setReload(false)
            }, 2000)
        }
    }, [reload])

    useEffect(() => {
        createCheckout()
    }, [])

    const createCheckout = async () => {
        const translations = {
            [locale.id]: {
                payButton: intl.formatMessage({
                    defaultMessage: 'Aggiungi carta di credito',
                    id: 'payButton',
                }),
            },
        }

        checkout = await AdyenCheckout({
            environment: adyenEnvironment.ADYEN_ENVIRONMENT,
            clientKey: adyenEnvironment.ADYEN_CLIENT_KEY,
            locale: locale.id,
            translations: translations,
            paymentMethodsConfiguration: {
                card: {
                    styles: {
                        border: '1px solid red',
                    },
                },
            },
            analytics: {
                analyticsData: {
                    applicationInfo: adyenPaymentMethods?.applicationInfo,
                },
            },
            onSubmit: (state, component) => {
                handleSubmit(state, component)
            },
            onAdditionalDetails: (state) => handleAdditionalDetails(state),
        })

        const card = checkout.create('card', {
            type: 'card',
            hasHolderName: true,
            holderNameRequired: true,
            showPayButton: true,
            _disableClickToPay: true,
        })

        card.mount(paymentContainer.current)
    }

    var shopperReference = customerId
    if (customer?.authType === 'registered') {
        if (customer?.c_adyenToken) {
            shopperReference = customer.c_adyenToken
        }
    }

    const paymentsDefaultConfig = {
        shopperReference: shopperReference,
        countryCode: currentCountry,
        channel: 'Web',
        returnUrl: `${window.location.pathname}/account`,
    }

    const handleAdditionalDetails = async (state) => {
        const token = await getTokenWhenReady()

        const response = await fetch(`/mobify/api/adyen/payments/details?siteId=${site.id}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(state),
        })

        const jsonResponse = await response.json()

        if (jsonResponse.isSuccessful === true) {
            updateRender()
            closeModal()
        } else {
            onOpen()
        }
    }

    const makePayment = async (paymentMethod, config = {}) => {
        const token = await getTokenWhenReady()

        const paymentsConfig = {...paymentsDefaultConfig, ...config}
        const paymentRequest = {...paymentsConfig, ...paymentMethod}

        await fetch(`/mobify/api/adyen/store-payment?siteId=${site.id}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                basketid: 'me',
                customerid: customer.customerId,
            },
            body: JSON.stringify({data: paymentRequest}),
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP Error: ${response.status}`)
                }
                return response.json()
            })
            .then((jsonResponse) => {
                if (jsonResponse.resultCode === 'Refused') {
                    onOpen()
                } else if (jsonResponse.resultCode === 'IdentifyShopper') {
                    let fromAction = checkout.createFromAction(jsonResponse.action)
                    fromAction.mount(fromActionRef.current)
                    setShowCard(false)
                } else {
                    updateRender()
                    closeModal()
                }
            })
            .catch((error) => {
                onOpen()
                console.error('Error:', error)
            })
    }

    const handleSubmit = (state) => {
        if (state.isValid) {
            const config = {}

            const cardData = {...state.data}

            makePayment(cardData, config)
        } else {
            onOpen()
        }
    }

    const securePaymentMessage = defineMessage({
        defaultMessage: 'Secure transaction',
        id: 'account.modal.add_card',
    })

    const mutationCallback = (mutations) => {
        const target = mutations?.[0].target?.firstChild
        if (target && target.classList.value.includes('adyen-checkout__card-input')) {
            const target = mutations?.[0].target?.firstChild
            if (target && !document.querySelector('.custom-text-add-card')) {
                const customElement = document.createElement('div')
                customElement.className = 'custom-text-add-card'
                customElement.style.display = 'flex'
                customElement.style.alignItems = 'center'
                customElement.style.marginTop = remCalc(12)
                const iconContainer = document.createElement('div')
                iconContainer.style.marginRight = remCalc(8)
                const iconElement = document.createElement('div')
                const root = createRoot(iconElement)
                root.render(<PaymentIcon boxSize={8} w={remCalc(20)} h={remCalc(22)} />)
                iconContainer.appendChild(iconElement)
                const textElement = document.createElement('span')
                textElement.textContent = formatMessage(securePaymentMessage)
                customElement.appendChild(iconContainer)
                customElement.appendChild(textElement)
                target.parentNode.insertBefore(customElement, target.nextSibling)
            }
        }
    }

    useMutationObserver(paymentContainer, mutationCallback, {
        childList: true,
        subtree: true,
    })

    return (
        <ToggleCard editing={true} isLoading={!adyenPaymentMethods || isSubmittingPayment}>
            <ErrorModal
                isOpen={isOpen}
                onClose={() => {
                    onClose()
                    closeModal()
                }}
                error={error}
            />
            <Stack __css={adyenStyle} gap={remCalc(0)}>
                {showCard && <div ref={paymentContainer}></div>}
                <div ref={fromActionRef} style={{display: 'flex', justifyContent: 'center'}}></div>
            </Stack>
        </ToggleCard>
    )
}

export default AddCard

AddCard.propTypes = {
    closeModal: PropTypes.func,
    updateRender: PropTypes.func,
}
